// Safari — desktop and iOS alike — has never shipped customized built-in
// elements, so `customElements.define('dialog-modal', …, { extends: 'dialog' })`
// throws NotSupportedError there and #DialogModal keeps only its native <dialog>
// API. Every caller guards with `typeof dialogModal.setContent === 'function'`
// and bails silently, which is why modal-opening buttons (e.g. "Add new address"
// in blocks/dashboard-addresses.liquid) did nothing at all on iPhone.
//
// So the shell is upgraded imperatively instead: the same public API
// (openModal/close/closeImmediately/setContent/setLoading/setSize) is assigned
// onto the plain <dialog data-dialog-modal>, which behaves identically in every
// browser and needs no polyfill.

const CLOSE_ANIMATION_MS = 300;

function upgradeDialogModal(dialog) {
  if (dialog.dialogModalUpgraded) return;
  dialog.dialogModalUpgraded = true;

  const modalContent = dialog.querySelector('.dialog-modal__content');
  // `dialog.close` is shadowed below, so keep a handle on the native one to
  // actually dismiss the element (stands in for the old `super.close()`).
  const nativeClose = HTMLDialogElement.prototype.close.bind(dialog);

  dialog.setSize = (size = 'medium') => {
    const allowedSizes = ['small', 'medium', 'large'];
    dialog.dataset.size = allowedSizes.includes(size) ? size : 'medium';
  };

  dialog.openModal = () => {
    // Disable background scrolling
    window.getWakaBodyScrollLock().lock();
    dialog.showModal();
    // Defer focus to content after modal is shown
    setTimeout(() => {
      if (modalContent) modalContent.focus();
    }, 0);
  };

  dialog.setLoading = () => {
    if (!modalContent) return;
    modalContent.innerHTML = `
      <div class="flex items-center justify-center p-12 w-full h-full min-h-[300px]">
        <svg class="animate-spin h-8 w-8 text-black" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      </div>
    `;
  };

  dialog.setContent = (html) => {
    if (!modalContent) return;
    modalContent.innerHTML = html;
    modalContent.setAttribute('tabindex', '-1');
    // Focus content after DOM is updated
    setTimeout(() => {
      if (modalContent) modalContent.focus();
    }, 0);
  };

  dialog.close = () => {
    dialog.classList.add('is-closing');

    // Clear content after animation
    setTimeout(() => {
      nativeClose();
      dialog.classList.remove('is-closing');

      // Restore background scrolling
      window.getWakaBodyScrollLock().unlock();

      if (modalContent) modalContent.innerHTML = '';

      dialog.setSize('medium');
    }, CLOSE_ANIMATION_MS);
  };

  dialog.closeImmediately = () => {
    nativeClose();
    dialog.classList.remove('is-closing');
    window.getWakaBodyScrollLock().unlock();

    if (modalContent) modalContent.innerHTML = '';

    dialog.setSize('medium');
  };

  dialog.setSize(dialog.dataset.size || 'medium');

  // Delegated so that `[data-dialog-close]` buttons inside injected content
  // (e.g. the Cancel button in the address dialog) close the modal too — a
  // querySelectorAll at upgrade time would only ever see the static shell's
  // close button.
  dialog.addEventListener('click', (event) => {
    if (event.target.closest('[data-dialog-close]')) {
      event.preventDefault();
      dialog.close();
      return;
    }

    // Backdrop click
    if (event.target === dialog) {
      event.preventDefault();
      dialog.close();
    }
  });

  // Handle Esc key (native cancel event)
  dialog.addEventListener('cancel', (event) => {
    event.preventDefault(); // Prevent immediate closing
    dialog.close(); // Trigger our animated close
  });

  // Close when item is successfully added to cart
  document.addEventListener('cart:updated', () => {
    if (dialog.open) dialog.close();
  });
}

document
  .querySelectorAll('dialog[data-dialog-modal]')
  .forEach(upgradeDialogModal);
